import { guardedClient as clientRequest } from '@repo/shared/helpers';
import {
	Group,
	GroupsApiResponse,
	Role,
	Type,
	Document,
	DocumentType,
	documentTypesApiResponseSchema,
	TasksApiResponse,
	LogApiResponse,
	EventsApiResponse,
	eventsApiResponseSchema,
	Tag,
	tagsApiResponseSchema,
	Form,
	formSchema,
	UsersApiResponse,
	Task,
	Requirement,
	requirementsSchema,
} from '@repo/shared/domain';
import { constructQuery } from '@repo/shared/helpers';

// ============ Groups List APIs ============

export const getGroups = async (query?: string): Promise<GroupsApiResponse> => {
	const { body } = await clientRequest.get<undefined, GroupsApiResponse>(`/api/groups${query ? `?${query}` : ''}`, {
		schema: undefined,
	});
	return body;
};

// ============ Group Detail APIs ============

export const getGroup = async (groupId: string): Promise<Group | null> => {
	try {
		const { body } = await clientRequest.get<undefined, Group>(`/api/groups/${groupId}`, {
			schema: undefined,
		});
		return body;
	} catch (error) {
		console.error('Failed to get group:', error);
		return null;
	}
};

export const getGroupRoles = async (groupId: string): Promise<Role[]> => {
	try {
		const { body } = await clientRequest.get<undefined, Role[]>(`/api/groups/${groupId}/roles`, {
			schema: undefined,
		});
		return body;
	} catch (error) {
		console.error('Failed to get group roles:', error);
		return [];
	}
};

export const getExtraDelegatedRoles = async (groupId: string): Promise<Role[]> => {
	try {
		const { body } = await clientRequest.get<undefined, Role[]>(`/api/groups/${groupId}/extra-delegated-roles`, {
			schema: undefined,
		});
		return body;
	} catch (error) {
		console.error('Failed to get extra delegated roles:', error);
		return [];
	}
};

export const getGroupUsers = async (
	groupId: string,
	params?: { page?: number; sort?: string; filter?: string },
): Promise<UsersApiResponse> => {
	const query = constructQuery({
		page: params?.page,
		sort: params?.sort,
		filter: params?.filter,
	});
	const { body } = await clientRequest.get<undefined, UsersApiResponse>(`/api/groups/${groupId}/users?${query}`, {
		schema: undefined,
	});
	return body;
};

export const getGroupForm = async (formSlug: string, language?: string): Promise<Form | undefined> => {
	const query = constructQuery({ language });
	try {
		const { body } = await clientRequest.get<typeof formSchema, Form>(`/api/forms/${formSlug}?${query}`, {
			schema: formSchema,
		});
		return body;
	} catch (error) {
		console.error('Failed to get form:', error);
		return undefined;
	}
};

export const getEventTypes = async (): Promise<{ data: Type[] }> => {
	const { body } = await clientRequest.get<undefined, { data: Type[] }>('/api/types?model=event', {
		schema: undefined,
	});
	return body;
};

export const getTags = async (): Promise<Tag[]> => {
	const { body } = await clientRequest.get<typeof tagsApiResponseSchema, Tag[]>('/api/tags', {
		schema: tagsApiResponseSchema,
	});
	return body;
};

export const getProjects = async (): Promise<{ data: { id: string; name: string }[] }> => {
	const { body } = await clientRequest.get<undefined, { data: { id: string; name: string }[] }>('/api/projects', {
		schema: undefined,
	});
	return body;
};

export const getGroupTaskDocuments = async (groupId: string, documentIds: string[]): Promise<Document[]> => {
	try {
		const { body } = await clientRequest.get<undefined, Document[]>(`/api/groups/${groupId}/files`, {
			schema: undefined,
		});
		// Filter documents to only those that belong to the task
		return body.filter((doc: Document) => documentIds.includes(doc._id));
	} catch (error) {
		console.error('Failed to get group task documents:', error);
		return [];
	}
};
